import { useSEO } from "@/hooks/useSEO";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Truck, MessageCircle } from "lucide-react";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import VehicleCard from "@/components/VehicleCard";
import HeroSlider from "@/components/HeroSlider";
import BookingBar from "@/components/BookingBar";
import FAQ from "@/components/FAQ";
import ReviewsCarousel from "@/components/ReviewsCarousel";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Clock, CreditCard, ArrowRight, MapPin, Star } from "lucide-react";

const NASSAU = "/faq-beach.jpg";

function ProgressNudge() {
  const { user } = useAuth();
  const [next, setNext] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/bookings/my").then((r) => {
      const today = new Date().toISOString().slice(0, 10);
      const upcoming = (r.data || [])
        .filter((b) => (b.status === "confirmed" || b.status === "pending_payment") && b.end_date >= today)
        .sort((a, b) => a.start_date.localeCompare(b.start_date))[0];
      setNext(upcoming || null);
    }).catch(() => {});
  }, [user]);

  if (!next) return null;
  const today = new Date().toISOString().slice(0, 10);
  const paid = next.status === "confirmed" || next.status === "completed";
  const done = [true, paid, !!next.contract_signed_at, paid && today >= next.start_date, !!next.return_submitted, next.status === "completed"].filter(Boolean).length;
  let action = null;
  if (!paid) action = { label: "Complete payment", to: "/account" };
  else if (!next.contract_signed_at) action = { label: "Sign your contract", to: `/contract/${next.id}` };
  else if (today < next.start_date) action = { label: "Express check-in", to: `/checkin/${next.id}` };
  else if (!next.return_submitted) action = { label: "Upload return photos", to: `/contract/${next.id}` };
  if (!action) return null;

  return (
    <div data-testid="home-progress-nudge" className="relative z-30 max-w-3xl mx-auto px-6 mt-8">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-primary/40 bg-card px-5 py-3.5 shadow-[0_8px_30px_rgba(224,36,52,0.08)]">
        <p className="text-sm font-ui text-foreground">
          <span className="gold-text font-semibold">{done} of 6 steps done</span>
          {" — "}{next.vehicle_name} · {fmtDate(next.start_date)} → {fmtDate(next.end_date)}
        </p>
        <Link data-testid="home-progress-nudge-link" to={action.to} className="inline-flex items-center gap-1.5 text-sm font-ui font-semibold text-primary hover:underline underline-offset-4">
          {action.label} <ArrowRight size={13} />
        </Link>
      </div>
    </div>
  );
}

export default function Home() {
  useSEO({ title: "Car Rental Nassau Bahamas | Free Hotel Delivery — Easy Drive", description: "Car rental & car hire in Nassau, Bahamas for island visitors and local drivers alike. Free hotel, villa & cruise port delivery, airport drop-off available, online booking with card, PayPal & Zelle.", path: "/" });
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, []);

  const marqueeVehicles = [...vehicles, ...vehicles];

  return (
    <div data-testid="home-page">
      <HeroSlider />
      <div className="relative z-30 -mt-14">
        <BookingBar />
      </div>
      <ProgressNudge />

      <section data-testid="home-trust-strip" className="max-w-7xl mx-auto px-6 lg:px-10 mt-10">
        <div className="rounded-2xl border border-primary/25 bg-card px-6 py-6 md:px-10 shadow-[0_8px_30px_rgba(224,36,52,0.06)]">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <ShieldCheck size={19} className="text-primary" />
              </span>
              <p className="font-display text-xl md:text-2xl tracking-tight">No hidden fees. <span className="italic gold-text">Ever.</span></p>
            </div>
            <ul className="flex flex-col sm:flex-row sm:flex-wrap gap-x-8 gap-y-2 text-sm font-ui text-foreground/80">
              <li data-testid="trust-point-0" className="flex items-center gap-2"><ShieldCheck size={14} className="gold-text shrink-0" /> No airport concession fees</li>
              <li data-testid="trust-point-1" className="flex items-center gap-2"><ShieldCheck size={14} className="gold-text shrink-0" /> No mandatory insurance add-ons</li>
              <li data-testid="trust-point-2" className="flex items-center gap-2"><ShieldCheck size={14} className="gold-text shrink-0" /> The price you see is the price you pay</li>
            </ul>
          </div>
        </div>
      </section>

      <section data-testid="home-why-direct" className="max-w-7xl mx-auto px-6 lg:px-10 mt-16">
        <div className="rounded-3xl border border-border bg-card p-8 md:p-10">
          <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary">Why book direct</p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <BadgeCheck size={20} className="gold-text" />
              <p className="mt-3 font-ui font-semibold text-sm">Best rate, guaranteed</p>
              <p className="mt-1.5 text-xs text-muted-foreground font-ui leading-relaxed">Booking sites take 15–25% commission. Book here and that saving is yours — plus loyalty points from rental #1.</p>
            </div>
            <div>
              <Truck size={20} className="gold-text" />
              <p className="mt-3 font-ui font-semibold text-sm">Free island delivery</p>
              <p className="mt-1.5 text-xs text-muted-foreground font-ui leading-relaxed">Hotel, villa, marina or airport — we bring the car to you anywhere on New Providence & Paradise Island.</p>
            </div>
            <div>
              <MessageCircle size={20} className="gold-text" />
              <p className="mt-3 font-ui font-semibold text-sm">Real humans, minutes away</p>
              <p className="mt-1.5 text-xs text-muted-foreground font-ui leading-relaxed">WhatsApp +1 (242) 813-1775 and talk to the team that actually holds the keys — no call-center scripts.</p>
            </div>
            <div>
              <Star size={20} className="gold-text" />
              <p className="mt-3 font-ui font-semibold text-sm">Locals' loyalty program</p>
              <p className="mt-1.5 text-xs text-muted-foreground font-ui leading-relaxed">Silver, Gold and Platinum tiers with rising discounts, deposit waivers and express check-in perks.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-border bg-card mt-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            ["Signature Fleet", "SUVs, sedans & convertibles, showroom-fresh"],
            ["Concierge Delivery", "Hotel, villa & cruise port — 24/7"],
            ["Five-Star Rated", ["Adored by island visitors", <Star key="s" size={12} className="inline gold-text ml-1" />]],
            ["Effortless Payments", "Card · PayPal · Zelle"],
          ].map(([t, s], i) => (
            <div key={i} data-testid={`home-stat-${i}`}>
              <p className="font-display text-2xl md:text-3xl tracking-tight gold-text">{t}</p>
              <p className="mt-1 text-xs md:text-sm text-muted-foreground font-ui">{s}</p>
            </div>
          ))}
        </div>
      </section>

      <section data-testid="home-vehicle-marquee" className="py-14 overflow-hidden border-b border-border bg-card">
        <p className="max-w-7xl mx-auto px-6 lg:px-10 text-[11px] font-ui tracking-[0.3em] uppercase text-primary mb-8">Now Available On The Lot</p>
        <div className="marquee flex gap-6 w-max px-6">
          {marqueeVehicles.map((v, i) => (
            <Link to={`/book/${v.id}`} key={`${v.id}-${i}`} data-testid={`marquee-vehicle-${i}`} className="group relative w-72 shrink-0 rounded-2xl overflow-hidden border border-border hover:border-primary/50 transition-colors duration-200">
              <img src={v.image_url} alt={v.name} className="w-full h-44 object-cover" loading="lazy" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-4 pt-10">
                <p className="font-display text-lg tracking-tight text-white">{v.name}</p>
                <p className="font-ui text-sm text-white/80">{fmtMoney(v.price_per_day)} <span className="text-white/60">/ day</span></p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section data-testid="home-how-it-works" className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: CreditCard, n: "1", t: "Book online", d: "Pick your dates and vehicle, upload your documents and pay securely — confirmation lands in your inbox in minutes." },
            { icon: MapPin, n: "2", t: "We deliver", d: "Hotel, villa or cruise port — your vehicle arrives detailed, sanitized and fueled, with a digital contract signed from your phone." },
            { icon: Clock, n: "3", t: "Drive & return", d: "Explore the island at your own pace, then hand back the keys — your $150 deposit is released on return." },
          ].map(({ icon: Icon, n, t, d }) => (
            <div key={n} data-testid={`how-step-${n}`} className="relative bg-card border border-border rounded-2xl p-7 transition-transform duration-300 hover:-translate-y-1 hover:border-primary/40">
              <span className="absolute top-5 right-6 font-display text-4xl text-primary/15">{n}</span>
              <div className="w-11 h-11 rounded-full border border-primary/30 flex items-center justify-center">
                <Icon size={18} className="gold-text" />
              </div>
              <h3 className="mt-4 font-display text-2xl tracking-tight">{t}</h3>
              <p className="mt-2.5 text-sm text-muted-foreground leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section data-testid="home-locals-section" className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-4">
        <div className="rounded-3xl border border-border bg-card p-8 md:p-12 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
          <div>
            <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary">For Nassau Locals</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight leading-tight">
              Live on the island? <span className="italic gold-text">We rent to you too.</span>
            </h2>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed font-ui max-w-lg">
              Weekend errands, airport runs, family visiting from abroad, or your own car in the shop — rent a clean, reliable vehicle by the day with your Bahamian driver's license. Same transparent rates as everyone else: the price you see is the price you pay.
            </p>
            <ul className="mt-5 space-y-2.5 text-sm text-foreground/80 font-ui">
              <li data-testid="locals-point-0" className="flex items-center gap-2.5"><ShieldCheck size={14} className="gold-text shrink-0" /> Bahamian driver's license accepted — no passport needed</li>
              <li data-testid="locals-point-1" className="flex items-center gap-2.5"><ShieldCheck size={14} className="gold-text shrink-0" /> 2-day minimum for local renters</li>
              <li data-testid="locals-point-2" className="flex items-center gap-2.5"><ShieldCheck size={14} className="gold-text shrink-0" /> Same rates as visitors — no local markup, no hidden fees</li>
              <li data-testid="locals-point-3" className="flex items-center gap-2.5"><ShieldCheck size={14} className="gold-text shrink-0" /> Pick up at our West Bay Street office, or we deliver</li>
            </ul>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <Link to="/fleet">
                <Button data-testid="locals-fleet-cta" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui px-7 h-11">
                  See Local Rates <ArrowRight size={15} className="ml-2" />
                </Button>
              </Link>
              <Link data-testid="locals-policies-link" to="/policies" className="text-sm font-ui font-semibold text-primary hover:underline underline-offset-4">
                Local renter policies →
              </Link>
            </div>
          </div>
          <LocalsFleetSlider vehicles={vehicles} />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary">The Collection</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl tracking-tight">
              Select your <span className="italic gold-text">signature ride</span>
            </h2>
          </div>
          <Link data-testid="home-view-all-fleet-link" to="/fleet" className="text-sm font-ui text-muted-foreground hover:text-primary transition-colors duration-200 flex items-center gap-2">
            View the full fleet <ArrowRight size={15} />
          </Link>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
          {vehicles.slice(0, 3).map((v, i) => (
            <div key={v.id} className={`fade-up fade-up-${i + 1}`}>
              <VehicleCard vehicle={v} testid={`home-vehicle-card-${v.id}`} bestValue={v.price_per_day === Math.min(...vehicles.map((x) => x.price_per_day))} />
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: ShieldCheck, t: "Complete Peace of Mind", d: "Every rental includes insurance, round-the-clock roadside assistance, and a digital contract you sign from your phone before you land." },
            { icon: Clock, t: "Delivered to Your Doorstep", d: "We meet you at the cruise port, Cable Beach or your hotel — on island time, yet never late. Flying out? Drop the car at the airport, free." },
            { icon: CreditCard, t: "Payment, Your Way", d: "Secure card checkout online, or settle by PayPal and Zelle. Every reservation confirmed instantly by email." },
          ].map(({ icon: Icon, t, d }, i) => (
            <div key={i} data-testid={`home-feature-${i}`} className="bg-card border border-border rounded-2xl p-8 transition-transform duration-300 hover:-translate-y-1 hover:border-primary/40">
              <div className="w-11 h-11 rounded-full border border-primary/30 flex items-center justify-center">
                <Icon size={18} className="gold-text" />
              </div>
              <h3 className="mt-5 font-display text-2xl tracking-tight">{t}</h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <ReviewsCarousel />

      <FAQ />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary">Our Home</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl tracking-tight leading-tight">
              Born and raised in the heart of <span className="italic gold-text">Nassau, Bahamas</span>
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed max-w-lg">
              From the pastel streets of downtown to the powder sands of Cable Beach
              and the bridges of Paradise Island — discover the Bahamas entirely at
              your own pace. Every vehicle is detailed, sanitized and fueled before
              it reaches you.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-foreground/80 font-ui">
              <li className="flex items-center gap-2.5"><MapPin size={14} className="gold-text" /> Airport drop-off at Lynden Pindling Intl Airport (NAS)</li>
              <li className="flex items-center gap-2.5"><MapPin size={14} className="gold-text" /> Delivery to hotels, villas & the cruise port</li>
              <li className="flex items-center gap-2.5"><MapPin size={14} className="gold-text" /> Local secrets & island maps with every rental</li>
            </ul>
          </div>
          <div className="order-1 lg:order-2 rounded-2xl overflow-hidden border border-border">
            <img src={NASSAU} alt="Nassau Bahamas shoreline" className="w-full h-full max-h-[80vh] object-cover" loading="lazy" />
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 text-left flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight max-w-xl leading-tight">
            Your island story begins <span className="italic gold-text">behind the wheel</span>
          </h2>
          <Link to="/fleet">
            <Button data-testid="home-cta-book-button" size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui px-10 h-12">
              Book Your Vehicle <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>    </div>
  );
}


function LocalsFleetSlider({ vehicles }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (vehicles.length < 2) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % vehicles.length), 6000);
    return () => clearInterval(t);
  }, [vehicles.length]);
  if (!vehicles.length) return null;
  return (
    <div className="rounded-2xl overflow-hidden border border-border bg-gradient-to-br from-stone-50 to-stone-100 h-80 lg:h-96 relative" data-testid="locals-vehicle-slider">
      {vehicles.map((v, i) => (
        <Link key={v.id} to={`/fleet/${v.id}`} data-testid={`locals-slide-${v.id}`} className={`absolute inset-0 flex flex-col items-center justify-center text-center transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
          <img src={v.image_url} alt={`${v.name} — car rental for Nassau locals`} className="w-full h-60 lg:h-72 object-contain px-2" loading="lazy" />
          <p className="mt-3 text-sm font-ui font-semibold text-foreground">{v.name}</p>
          <p className="text-xs font-ui text-primary font-semibold">{fmtMoney(v.price_per_day)}/day</p>
        </Link>
      ))}
      <button data-testid="locals-slide-prev" onClick={() => setIdx((i) => (i - 1 + vehicles.length) % vehicles.length)} className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 border border-border text-foreground hover:bg-white flex items-center justify-center z-10" aria-label="Previous vehicle">‹</button>
      <button data-testid="locals-slide-next" onClick={() => setIdx((i) => (i + 1) % vehicles.length)} className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 border border-border text-foreground hover:bg-white flex items-center justify-center z-10" aria-label="Next vehicle">›</button>
      <div className="absolute bottom-3 inset-x-0 flex justify-center gap-1.5 z-10">
        {vehicles.map((v, i) => (
          <button key={v.id} data-testid={`locals-dot-${i}`} onClick={() => setIdx(i)} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === idx ? "bg-primary" : "bg-stone-300"}`} aria-label={`Show ${v.name}`} />
        ))}
      </div>
    </div>
  );
}
